import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cake, Award, PartyPopper, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Avatar } from "@/components/Avatar";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

/** Every staff birthday + work anniversary in the current month, grouped by day. */
export function CelebrationsMonthCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/humi/celebrations-month").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const byDay = (data.items || []).reduce((acc, i) => {
    (acc[i.day] = acc[i.day] || []).push(i);
    return acc;
  }, {});
  const days = Object.keys(byDay).map(Number).sort((a, b) => a - b);

  return (
    <Card className="overflow-hidden border-slate-200 p-0 dark:border-slate-700" data-testid="humi-celebrations-month">
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
        <PartyPopper className="h-4 w-4 text-violet-500" />
        <h3 className="font-heading text-sm font-semibold text-slate-800 dark:text-slate-100">
          Celebrations in {MONTH_NAMES[(data.month || 1) - 1]}
        </h3>
        <span className="ml-auto text-[11px] text-slate-400" data-testid="humi-celebrations-month-count">
          {(data.items || []).length}
        </span>
      </div>

      {data.my_birthday_missing && (
        <Link to="/humi/profile" data-testid="humi-celebrations-self-nudge"
          className="flex items-center gap-2 border-b border-amber-100 bg-amber-50/70 px-4 py-2 text-xs text-amber-800 transition-colors hover:bg-amber-100 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          We don&apos;t have your birthday yet — add it to your profile so the team can celebrate.
        </Link>
      )}

      {data.is_hr && data.missing_count > 0 && (
        <Link to="/humi/people?dates=missing" data-testid="humi-celebrations-hr-chase"
          className="flex items-center gap-2 border-b border-amber-100 bg-amber-50/70 px-4 py-2 text-xs text-amber-800 transition-colors hover:bg-amber-100 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {data.missing_count} staff {data.missing_count === 1 ? "member is" : "members are"} missing a
          birthday or start date — open the list to chase them.
        </Link>
      )}

      {days.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-slate-400" data-testid="humi-celebrations-month-empty">
          No birthdays or work anniversaries this month.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {days.map((day) => (
            <li key={day} className={`flex gap-3 px-4 py-2.5 ${day === data.today ? "bg-violet-50/70 dark:bg-violet-500/10" : ""}`}
                data-testid={`humi-celebrations-day-${day}`}>
              <div className="w-10 shrink-0 text-center">
                <div className={`text-sm font-bold ${day === data.today ? "text-violet-700 dark:text-violet-300" : "text-slate-700 dark:text-slate-200"}`}>{day}</div>
                <div className="text-[9px] uppercase tracking-wide text-slate-400">
                  {day === data.today ? "today" : MONTH_NAMES[(data.month || 1) - 1].slice(0, 3)}
                </div>
              </div>
              <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                {byDay[day].map((i) => {
                  const Icon = i.kind === "birthday" ? Cake : Award;
                  return (
                    <span key={`${i.user_id}-${i.kind}`}
                      data-testid={`humi-celebration-${i.kind}-${i.user_id}`}
                      title={i.kind === "birthday" ? `${i.name}'s birthday` : `${i.name} started ${i.started}`}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200">
                      <Avatar name={i.name} src={i.avatar_url} size="sm" />
                      <span className="font-medium">{i.name}</span>
                      <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400">
                        <Icon className="h-3 w-3" />
                        {i.kind === "birthday" ? "Birthday" : `${i.years} yr${i.years === 1 ? "" : "s"}`}
                      </span>
                    </span>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export default CelebrationsMonthCard;
